import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { AREAS, CAMPO, CONFIGURACION, GENERAL, type EntradaMenu } from '@/app/navegacion'
import { useSesion } from '@/modules/auth/useSesion'
import { useSoyDeCampo } from '@/modules/campo/useSoyDeCampo'
import { NOMBRE_ROL } from '@/shared/dominio/roles'
import { MarcaMega } from '@/shared/marca/MarcaMega'
import {
  IconoAlistamiento, IconoArchivo, IconoAuditoria, IconoCartera, IconoChat, IconoClientes, IconoColaborador, IconoColapsar, IconoExpandir,
  IconoCotizacion, IconoFactura,
  IconoHerramienta, IconoInventario, IconoMapa, IconoMatriz, IconoMiTrabajo,
  IconoPunto, IconoSalir, IconoSobrantes, IconoSolicitudes, IconoTablero,
  IconoOperacion, IconoComercial, IconoFinanciera, IconoBodega, IconoConfiguracion,
} from '@/shared/ui/Iconos/Iconos'
import { iniciales } from '@/shared/utilidades/formato'

import estilos from './MenuLateral.module.css'

/** Cada entrada dice qué icono la representa; aquí se resuelve a su dibujo. */
const ICONOS: Record<string, (p: { className?: string }) => React.ReactElement> = {
  tablero: IconoTablero,
  miTrabajo: IconoMiTrabajo,
  solicitudes: IconoSolicitudes,
  matriz: IconoMatriz,
  chat: IconoChat,
  herramienta: IconoHerramienta,
  mapa: IconoMapa,
  archivo: IconoArchivo,
  clientes: IconoClientes,
  inventario: IconoInventario,
  cotizacion: IconoCotizacion,
  factura: IconoFactura,
  colaborador: IconoColaborador,
  cartera: IconoCartera,
  alistamiento: IconoAlistamiento,
  auditoria: IconoAuditoria,
  sobrantes: IconoSobrantes,
}

function Icono({ clave }: { clave: string }) {
  const Dibujo = ICONOS[clave] ?? IconoPunto
  return <Dibujo />
}

type PropsMenuLateral = {
  abierto: boolean
  onCerrar: () => void
  /** Cuántas decisiones esperan en cada módulo, por ruta. */
  pendientes?: Record<string, number>
  colapsado?: boolean
  onAlternarColapso?: () => void
  onDescolapsar?: () => void
}

export function MenuLateral({
  abierto,
  onCerrar,
  pendientes = {},
  colapsado = false,
  onAlternarColapso,
}: PropsMenuLateral) {
  const { puede, perfil, salir } = useSesion()
  const deCampo = useSoyDeCampo()
  const [hoverExpandido, setHoverExpandido] = useState(false)

  // En móvil/pantallas táctiles (<= 960px) NUNCA se colapsa
  const [esEscritorio, setEsEscritorio] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth > 960 : true
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(min-width: 961px)')
    const actualizar = (e: MediaQueryListEvent | MediaQueryList) => {
      setEsEscritorio(e.matches)
      if (!e.matches) setHoverExpandido(false)
    }
    actualizar(mql)
    mql.addEventListener('change', actualizar)
    return () => mql.removeEventListener('change', actualizar)
  }, [])

  // El menú está efectivamente colapsado SOLO en pantallas de escritorio
  const esColapsado = esEscritorio && colapsado && !hoverExpandido

  const soloCampo = perfil?.rol === 'operario'
  const { pathname, search } = useLocation()
  const rutaActual = pathname + search

  const areaActual = AREAS.find((a) =>
    a.entradas.some((e) =>
      e.ruta.includes('?')
        ? rutaActual === e.ruta
        : pathname === e.ruta || pathname.startsWith(e.ruta + '/'),
    ),
  )

  const [areasAbiertas, setAreasAbiertas] = useState<string[]>(() =>
    areaActual ? [areaActual.clave] : ['operacion'],
  )

  function alternarArea(clave: string) {
    setAreasAbiertas((previas) =>
      previas.includes(clave) ? previas.filter((c) => c !== clave) : [...previas, clave],
    )
  }

  function puedeVer(entrada: EntradaMenu) {
    return puede(entrada.modulo, entrada.accion ?? 'ver')
  }

  function dibujarEnlace(entrada: EntradaMenu) {
    return (
      <NavLink
        key={entrada.ruta}
        to={entrada.ruta}
        end={entrada.ruta === '/'}
        onClick={onCerrar}
        title={entrada.etiqueta}
        className={({ isActive }) => `${estilos.enlace} ${isActive ? estilos.activo : ''}`}
      >
        <Icono clave={entrada.icono} />
        <span className={estilos.etiqueta}>{entrada.etiqueta}</span>
      </NavLink>
    )
  }

  const generalVisible = GENERAL.filter((e) => puedeVer(e) && (!e.soloCampo || deCampo))
  const configuracionVisible = CONFIGURACION.filter(puedeVer)

  // Mapeo de secciones principales para modo colapsado en escritorio
  const SECCIONES_PRINCIPALES = [
    {
      clave: 'tablero',
      ruta: '/',
      etiqueta: 'Tablero',
      IconoComp: IconoTablero,
      activo: pathname === '/',
      visible: puede('tablero', 'ver'),
    },
    {
      clave: 'operacion',
      ruta: '/ordenes',
      etiqueta: 'Operación',
      IconoComp: IconoOperacion,
      activo:
        pathname.startsWith('/ordenes') ||
        (pathname.startsWith('/plan-proyectos') && (!search || search.includes('vista=gantt') || search.includes('vista=curvas') || search.includes('vista=apus'))) ||
        pathname.startsWith('/chatbot') ||
        pathname.startsWith('/herramientas') ||
        pathname.startsWith('/servicios-finalizados'),
      visible: AREAS.find((a) => a.clave === 'operacion')?.entradas.some(puedeVer),
    },
    {
      clave: 'comercial',
      ruta: '/clientes',
      etiqueta: 'Comercial y Clientes',
      IconoComp: IconoComercial,
      activo: pathname.startsWith('/clientes') || pathname.startsWith('/cotizaciones') || (pathname.startsWith('/plan-proyectos') && search.includes('vista=presupuestos')),
      visible: AREAS.find((a) => a.clave === 'comercial')?.entradas.some(puedeVer),
    },
    {
      clave: 'financiera',
      ruta: '/financiera/cartera',
      etiqueta: 'Financiera',
      IconoComp: IconoFinanciera,
      activo: pathname.startsWith('/financiera') || pathname.startsWith('/facturas') || (pathname.startsWith('/plan-proyectos') && search.includes('vista=pyg')),
      visible: AREAS.find((a) => a.clave === 'financiera')?.entradas.some(puedeVer),
    },
    {
      clave: 'bodega',
      ruta: '/bodega/alistamientos',
      etiqueta: 'Bodega',
      IconoComp: IconoBodega,
      activo:
        pathname.startsWith('/bodega') ||
        pathname.startsWith('/inventario') ||
        pathname.startsWith('/maestros/bodega'),
      visible: AREAS.find((a) => a.clave === 'bodega')?.entradas.some(puedeVer),
    },
    {
      clave: 'configuracion',
      ruta: '/maestros/colaboradores',
      etiqueta: 'Configuración',
      IconoComp: IconoConfiguracion,
      activo: pathname.startsWith('/maestros/colaboradores') || pathname.startsWith('/auditoria'),
      visible: configuracionVisible.length > 0,
    },
  ]

  return (
    <>
      {abierto && (
        <div
          className={estilos.velo}
          onClick={onCerrar}
          onTouchEnd={(e) => {
            e.stopPropagation()
            onCerrar()
          }}
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          estilos.menu,
          abierto ? estilos.abierto : '',
          esColapsado ? estilos.colapsado : '',
          hoverExpandido && esEscritorio ? estilos.menuHoverExpandido : '',
          'no-imprimir',
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseEnter={() => {
          if (esEscritorio && colapsado) setHoverExpandido(true)
        }}
        onMouseLeave={() => {
          if (esEscritorio) setHoverExpandido(false)
        }}
      >
        <div className={estilos.cabecera}>
          <div className={estilos.marcaContenedor}>
            <MarcaMega tamanoLogo={esColapsado ? 30 : 26} soloLogo={esColapsado} />
          </div>

          {!esColapsado && esEscritorio && onAlternarColapso && (
            <button
              type="button"
              className={`${estilos.botonColapsar} ${colapsado ? estilos.botonColapsarFijado : ''
                }`}
              onClick={onAlternarColapso}
              title={
                colapsado
                  ? 'Menú lateral fijado en barra compacta (Clic para expandir)'
                  : 'Fijar menú colapsado (Ctrl+B)'
              }
              aria-label={colapsado ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
            >
              {colapsado ? (
                <IconoExpandir className={estilos.iconoColapsar} />
              ) : (
                <IconoColapsar className={estilos.iconoColapsar} />
              )}
            </button>
          )}
        </div>

        <nav className={`${estilos.navegacion} desplazable`} aria-label="Menú principal">
          {soloCampo ? (
            CAMPO.filter(puedeVer).map(dibujarEnlace)
          ) : esColapsado ? (
            /* Vista colapsada ÚNICAMENTE en escritorio */
            <div className={estilos.seccionesColapsadas}>
              {SECCIONES_PRINCIPALES.filter((s) => s.visible).map((s) => {
                const IconoSection = s.IconoComp
                return (
                  <NavLink
                    key={s.clave}
                    to={s.ruta}
                    title={s.etiqueta}
                    onClick={onCerrar}
                    className={`${estilos.botonSeccionColapsada} ${s.activo ? estilos.seccionActiva : ''
                      }`}
                  >
                    <IconoSection />
                  </NavLink>
                )
              })}
            </div>
          ) : (
            /* Vista completa (siempre en móvil o cuando no está colapsado en escritorio) */
            <>
              {generalVisible.length > 0 && (
                <>
                  <p className={estilos.grupo}>
                    General<span className={estilos.linea} aria-hidden="true" />
                  </p>
                  {generalVisible.map(dibujarEnlace)}
                </>
              )}

              <p className={estilos.grupo}>
                Áreas de la empresa<span className={estilos.linea} aria-hidden="true" />
              </p>

              {AREAS.map((area) => {
                const visibles = area.entradas.filter(puedeVer)
                if (visibles.length === 0) return null

                const abierta = areasAbiertas.includes(area.clave)

                return (
                  <div key={area.clave} className={abierta ? estilos.areaAbierta : undefined}>
                    <button
                      type="button"
                      className={estilos.areaCabecera}
                      onClick={() => alternarArea(area.clave)}
                      aria-expanded={abierta}
                    >
                      <span className={estilos.flecha} aria-hidden="true">
                        ▾
                      </span>
                      <span className={estilos.areaNombre}>{area.nombre}</span>
                    </button>

                    <div className={estilos.modulos}>
                      {visibles.map((entrada) => {
                        const cuantos = pendientes[entrada.ruta] ?? 0

                        return (
                          <NavLink
                            key={entrada.ruta}
                            to={entrada.ruta}
                            onClick={onCerrar}
                            className={() => {
                              const esActivo = entrada.ruta.includes('?')
                                ? rutaActual === entrada.ruta
                                : pathname === entrada.ruta && (!search || search === '?vista=gantt')
                              return [
                                estilos.modulo,
                                esActivo ? estilos.moduloActivo : '',
                                entrada.lista ? '' : estilos.pendiente,
                              ]
                                .filter(Boolean)
                                .join(' ')
                            }}
                          >
                            <Icono clave={entrada.icono} />
                            <span className={estilos.etiqueta}>{entrada.etiqueta}</span>
                            {cuantos > 0 && <span className={estilos.contador}>{cuantos}</span>}
                          </NavLink>
                        )
                      })}
                    </div>
                  </div>
                )
              })}

              {configuracionVisible.length > 0 && (
                <>
                  <p className={estilos.grupo}>
                    Configuración<span className={estilos.linea} aria-hidden="true" />
                  </p>
                  {configuracionVisible.map(dibujarEnlace)}
                </>
              )}
            </>
          )}
        </nav>

        {perfil && (
          <div className={estilos.pie}>
            <span className={estilos.avatar} aria-hidden="true">
              {iniciales(perfil.nombre)}
            </span>

            {!esColapsado && (
              <>
                <span className={estilos.quien}>
                  <span className={estilos.nombre}>{perfil.nombre}</span>
                  <span className={estilos.cargo}>{NOMBRE_ROL[perfil.rol]}</span>
                </span>

                <button
                  type="button"
                  className={estilos.salir}
                  onClick={() => void salir()}
                  title="Salir"
                  aria-label="Salir"
                >
                  <IconoSalir />
                </button>
              </>
            )}
          </div>
        )}
      </aside>
    </>
  )
}
